import * as React from 'react';
import { cn } from '../lib/utils';

/**
 * Skeleton suite — replaces "show a spinner during data load."
 *
 * The shimmer animation is defined in tokens/motion.css (`.tyndo-shimmer`)
 * and respects `prefers-reduced-motion`. Skeletons should be the default
 * loading state for any content that has stable structure (tables, KPI grids,
 * charts, lists). Spinners stay for action-feedback only (a button while
 * waiting on a mutation).
 */

interface BaseProps {
  className?: string;
}

export function Skel({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        'block rounded tyndo-shimmer',
        className
      )}
      {...props}
    />
  );
}

export interface TableSkeletonProps extends BaseProps {
  rows?: number;
  columns?: number;
}

/** Mimics a DataTable while data loads. */
export function TableSkeleton({
  rows = 6,
  columns = 5,
  className,
}: TableSkeletonProps) {
  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-center gap-3 border-b border-border bg-surface-sunken/60 px-4 py-2">
        {Array.from({ length: columns }).map((_, i) => (
          <Skel
            key={i}
            className={`h-3 ${i === 0 ? 'w-24' : i === columns - 1 ? 'w-16' : 'w-20'}`}
          />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex items-center gap-3 border-b border-border px-4 py-3"
        >
          {Array.from({ length: columns }).map((_, c) => (
            <Skel
              key={c}
              className={`h-4 ${c === 0 ? 'w-32' : c === columns - 1 ? 'w-12' : 'w-24'}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface KpiSkeletonProps extends BaseProps {
  count?: number;
}

/** A grid of KPI card skeletons. */
export function KpiSkeleton({ count = 4, className }: KpiSkeletonProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4',
        className
      )}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col gap-2 rounded-lg border border-border bg-surface-raised p-4"
        >
          <div className="flex items-center justify-between">
            <Skel className="h-3 w-16" />
            <Skel className="h-4 w-4 rounded-full" />
          </div>
          <Skel className="h-9 w-24" />
          <Skel className="h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

export interface ListSkeletonProps extends BaseProps {
  rows?: number;
}

/** A vertical list of records (e.g., master pane in master-detail). */
export function ListSkeleton({ rows = 5, className }: ListSkeletonProps) {
  return (
    <div className={cn('divide-y divide-border', className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 px-3 py-3">
          <Skel className="h-9 w-9 rounded-md shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skel className="h-3 w-3/4" />
            <Skel className="h-3 w-1/2" />
          </div>
          <Skel className="h-4 w-12" />
        </div>
      ))}
    </div>
  );
}

export interface ChartSkeletonProps extends BaseProps {
  height?: number;
}

/** A chart frame skeleton with a faint axis suggestion. */
export function ChartSkeleton({ height = 220, className }: ChartSkeletonProps) {
  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-lg border border-border bg-surface-raised p-4',
        className
      )}
      style={{ height }}
    >
      <div className="mb-3 flex items-center justify-between">
        <Skel className="h-3 w-32" />
        <div className="flex gap-1">
          <Skel className="h-2 w-2 rounded-full" />
          <Skel className="h-3 w-12" />
          <Skel className="h-2 w-2 rounded-full" />
          <Skel className="h-3 w-12" />
        </div>
      </div>
      <Skel
        className="absolute bottom-4 left-4 right-4 rounded-md"
        style={{ height: 'calc(100% - 60px)' }}
      />
    </div>
  );
}
